// "Does this Company have stock of its OWN on file?" — the existence probe the
// Assembly control asks for two rework-only decisions: which origin a new rework
// row opens on, and whether the rework's "no stock on file, email us" dead end is
// spelled out beside the kit picker.
//
// It never decides whether a stock question is ASKED: the `catalog_sku` origin
// and the kit-or-SKU picker are always offered, and an empty catalog is said
// inside the picker itself (CompanyItemPicker's empty-catalog line). Both callers
// ask it here rather than each hand-rolling the query — a `staleTime` or a
// `placeholderData` added to one copy and not the other is how two answers to one
// question start.
//
// It is deliberately NOT the picker's list. The SEARCH belongs to each picker,
// which owns its own query because Nexus pages its answer (CompanyItemPicker) —
// one shared list would let the row being typed in truncate every other row's
// dropdown. This is the unsearched first page, and its cache key is shared with
// the pickers' empty-query key on purpose: it is literally the same request, so
// TanStack Query serves both from one fetch.
//
// Non-price company data, so R4 would permit a cache, but none is held: like the
// pickers, it revalidates against Nexus on every mount.

import { useQuery } from '@tanstack/react-query';

import { isPartnerStock } from 'shared';

import { getCompanyItems } from '../api/company-items.js';

export function useCompanyStock(opts: { enabled: boolean }): boolean {
  const { data } = useQuery({
    // Matching CompanyItemPicker's key EXACTLY. TanStack hashes the whole array,
    // so any other shape would never match the picker's even for the identical
    // empty-query request, and the shared fetch this hook exists for would
    // quietly become two.
    queryKey: ['company-items', ''],
    queryFn: () => getCompanyItems(),
    enabled: opts.enabled,
    // Zero, overriding the app-wide default, as the picker's own query does.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  // The partner's OWN stock only: every read carries Argo's offerable stock too,
  // and counting it would answer "yes" for every Company. A page cut off at the
  // cap with none of the partner's rows on it cannot say "none" — their items may
  // sort past Argo's — so it answers yes rather than claim a dead end it cannot
  // see.
  if (!data) return false;
  return data.items.some(isPartnerStock) || data.truncated;
}
